import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/clinic/Navbar";
import Footer from "@/components/clinic/Footer";
import { applySeo, buildSiteGraph } from "@/lib/seo";
import { useToast } from "@/hooks/use-toast";
import { useLiveClinic } from "@/lib/clinicLive";
import type { ClinicHoursRow, ClinicNAP } from "@/lib/clinic";
import ClinicNAPPreview from "@/components/admin/ClinicNAPPreview";
import { validateContactFields } from "@/lib/phoneValidation";
import { validateSiteGraph, type SchemaIssue } from "@/lib/schemaValidation";
import { AlertCircle, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { runNapPreviewCheck, type NapPreviewResult } from "@/lib/napPreviewCheck";

const settingsSchema = z.object({
  name: z.string().trim().min(2).max(120),
  short_name: z.string().trim().min(2).max(60),
  alternate_name: z.string().trim().max(120).optional().or(z.literal("")),
  tagline: z.string().trim().max(280).optional().or(z.literal("")),
  street: z.string().trim().min(2).max(160),
  locality: z.string().trim().min(2).max(80),
  region: z.string().trim().min(2).max(80),
  postal_code: z.string().trim().min(3).max(20),
  country: z.string().trim().min(2).max(80),
  country_code: z.string().trim().length(2),
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  phone_international: z.string().trim().min(5).max(30),
  phone_display: z.string().trim().min(5).max(30),
  whatsapp_number: z.string().trim().max(30).optional().or(z.literal("")),
  whatsapp_display: z.string().trim().max(30).optional().or(z.literal("")),
  whatsapp_url: z.string().trim().url().max(200).optional().or(z.literal("")),
  booking_note: z.string().trim().max(120).optional().or(z.literal("")),
  website_url: z.string().trim().url().max(200).optional().or(z.literal("")),
  website_display: z.string().trim().max(80).optional().or(z.literal("")),
  instagram_url: z.string().trim().url().max(200).optional().or(z.literal("")),
  instagram_handle: z.string().trim().max(60).optional().or(z.literal("")),
  booking_url: z.string().trim().url().max(200).optional().or(z.literal("")),
});

interface FormState {
  id: string | null;
  name: string;
  short_name: string;
  alternate_name: string;
  tagline: string;
  street: string;
  locality: string;
  region: string;
  postal_code: string;
  country: string;
  country_code: string;
  latitude: string;
  longitude: string;
  phone_international: string;
  phone_display: string;
  whatsapp_number: string;
  whatsapp_display: string;
  whatsapp_url: string;
  booking_note: string;
  website_url: string;
  website_display: string;
  instagram_url: string;
  instagram_handle: string;
  booking_url: string;
  hours: ClinicHoursRow[];
}

const fromLive = (live: ReturnType<typeof useLiveClinic>): Omit<FormState, "id"> => ({
  name: live.name,
  short_name: live.shortName,
  alternate_name: live.alternateName ?? "",
  tagline: live.tagline ?? "",
  street: live.address.street,
  locality: live.address.locality,
  region: live.address.region,
  postal_code: live.address.postalCode,
  country: live.address.country,
  country_code: live.address.countryCode,
  latitude: String(live.geo.latitude ?? ""),
  longitude: String(live.geo.longitude ?? ""),
  phone_international: live.phone.international,
  phone_display: live.phone.display,
  whatsapp_number: live.whatsapp.number ?? "",
  whatsapp_display: live.whatsapp.display ?? "",
  whatsapp_url: live.whatsapp.url ?? "",
  booking_note: live.bookingNote ?? "",
  website_url: live.social.website ?? "",
  website_display: live.social.websiteDisplay ?? "",
  instagram_url: live.social.instagram ?? "",
  instagram_handle: live.social.instagramHandle ?? "",
  booking_url: live.bookingUrl ?? "",
  hours: live.hours,
});

const ClinicSettings = () => {
  const live = useLiveClinic();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>({ id: null, ...fromLive(live) });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    applySeo({
      title: "Clinic settings — Dr Vahe Clinic Admin",
      description: "Edit the clinic NAP and structured data.",
      path: "/admin/clinic-settings",
    });
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    return () => robots?.setAttribute("content", "index, follow");
  }, []);

  // Load the row id (we always edit the most-recent row).
  useEffect(() => {
    supabase
      .from("clinic_settings")
      .select("id")
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setForm((prev) => ({ ...prev, id: data?.id ?? null, ...fromLive(live) }));
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const setHour = (i: number, patch: Partial<ClinicHoursRow>) =>
    setForm((p) => ({
      ...p,
      hours: p.hours.map((row, idx) => (idx === i ? { ...row, ...patch } : row)),
    }));

  // Build a ClinicNAP from the in-progress form so the preview reflects
  // every keystroke without needing to save.
  const previewClinic = useMemo<ClinicNAP>(
    () => ({
      name: form.name || live.name,
      legalName: live.legalName,
      alternateName: form.alternate_name || live.alternateName,
      shortName: form.short_name || live.shortName,
      tagline: form.tagline || live.tagline,
      founded: live.founded,
      address: {
        street: form.street || live.address.street,
        locality: form.locality || live.address.locality,
        region: form.region || live.address.region,
        postalCode: form.postal_code || live.address.postalCode,
        country: form.country || live.address.country,
        countryCode: form.country_code || live.address.countryCode,
      },
      geo: {
        latitude: form.latitude ? Number(form.latitude) : live.geo.latitude,
        longitude: form.longitude ? Number(form.longitude) : live.geo.longitude,
      },
      phone: {
        international: form.phone_international || live.phone.international,
        display: form.phone_display || live.phone.display,
      },
      whatsapp: {
        number: form.whatsapp_number || "",
        display: form.whatsapp_display || "",
        url: form.whatsapp_url || "",
      },
      hours: form.hours,
      bookingNote: form.booking_note || live.bookingNote,
      social: {
        website: form.website_url || "",
        websiteDisplay: form.website_display || "",
        instagram: form.instagram_url || "",
        instagramHandle: form.instagram_handle || "",
      },
      bookingUrl: form.booking_url || live.bookingUrl,
    }),
    [form, live],
  );

  const previewGraph = useMemo(() => buildSiteGraph(previewClinic), [previewClinic]);

  // Inline contact validation — drives field error styles, preview badge,
  // and disables Save when anything is malformed.
  const contactErrors = useMemo(
    () =>
      validateContactFields({
        phone_international: form.phone_international,
        phone_display: form.phone_display,
        whatsapp_number: form.whatsapp_number,
        whatsapp_url: form.whatsapp_url,
      }),
    [form.phone_international, form.phone_display, form.whatsapp_number, form.whatsapp_url],
  );

  // Static-data validation — runs against the live preview graph so the
  // admin sees missing or malformed JSON-LD fields BEFORE saving.
  const schemaValidation = useMemo(() => validateSiteGraph(previewGraph), [previewGraph]);

  // Form fields that are referenced by at least one schema error — used
  // to highlight inputs in the form even when contact validation passes.
  const schemaErrorFields = useMemo(() => {
    const set = new Set<string>();
    for (const issue of schemaValidation.issues) {
      if (issue.severity === "error" && issue.formField) set.add(issue.formField);
    }
    return set;
  }, [schemaValidation]);

  // NAP cross-check between the form values and the JSON-LD they generate.
  // Auto-runs once on mount and whenever the preview graph changes; the
  // admin can also trigger it manually with the "Re-run NAP check" button.
  const [napCheck, setNapCheck] = useState<NapPreviewResult>(() =>
    runNapPreviewCheck(previewClinic, previewGraph),
  );
  const [napChecking, setNapChecking] = useState(false);

  useEffect(() => {
    setNapCheck(runNapPreviewCheck(previewClinic, previewGraph));
  }, [previewClinic, previewGraph]);

  const reRunNapCheck = () => {
    setNapChecking(true);
    // Small delay so the button visually reflects the action.
    window.setTimeout(() => {
      const result = runNapPreviewCheck(previewClinic, previewGraph);
      setNapCheck(result);
      setNapChecking(false);
      toast({
        title: result.ok ? "NAP check passed" : `NAP check: ${result.errorCount} mismatch${result.errorCount === 1 ? "" : "es"}`,
        description: result.ok
          ? "Form values and JSON-LD agree on every NAP field."
          : "See the highlighted rows in the NAP check panel for details.",
        variant: result.ok ? "default" : "destructive",
      });
    }, 120);
  };

  // Diff: which fields differ from currently-saved live values.
  const isDirty = useMemo(() => {
    return (
      previewClinic.name !== live.name ||
      previewClinic.address.street !== live.address.street ||
      previewClinic.address.postalCode !== live.address.postalCode ||
      previewClinic.phone.international !== live.phone.international ||
      previewClinic.phone.display !== live.phone.display ||
      previewClinic.whatsapp.number !== live.whatsapp.number ||
      JSON.stringify(previewClinic.hours) !== JSON.stringify(live.hours)
    );
  }, [previewClinic, live]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (contactErrors.hasError) {
      toast({
        title: "Fix contact details first",
        description: "Phone or WhatsApp formatting is invalid — see the highlighted fields.",
        variant: "destructive",
      });
      return;
    }
    if (schemaValidation.hasError) {
      const first = schemaValidation.issues.find((i) => i.severity === "error");
      toast({
        title: `Fix ${schemaValidation.errorCount} JSON-LD ${schemaValidation.errorCount === 1 ? "error" : "errors"}`,
        description: first ? `${first.node} · ${first.path}: ${first.message}` : "See the validation panel.",
        variant: "destructive",
      });
      return;
    }
    const validated = settingsSchema.safeParse({
      ...form,
      latitude: form.latitude ? Number(form.latitude) : null,
      longitude: form.longitude ? Number(form.longitude) : null,
    });
    if (!validated.success) {
      const first = validated.error.issues[0];
      toast({
        title: "Check your inputs",
        description: `${first?.path.join(".")}: ${first?.message}`,
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...validated.data,
        hours: JSON.parse(JSON.stringify(form.hours)),
      };
      const query = form.id
        ? supabase.from("clinic_settings").update(payload as never).eq("id", form.id)
        : supabase.from("clinic_settings").insert(payload as never);
      const { error } = await query;
      if (error) throw error;
      toast({
        title: "Settings saved",
        description: "NAP widgets and JSON-LD have been regenerated live.",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed";
      toast({ title: "Could not save", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const inputCls =
    "mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-accent/40";
  const labelCls = "block text-[10px] tracking-[0.3em] uppercase text-muted-foreground";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <article className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link
            to="/"
            className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 transition-colors"
          >
            ← Back to clinic
          </Link>
          <header className="mt-8 mb-10">
            <span className="text-[10px] tracking-[0.35em] uppercase text-accent font-medium">
              Admin · Local SEO
            </span>
            <h1 className="font-display text-4xl md:text-5xl mt-4 text-foreground">
              Clinic <span className="italic">settings</span>
            </h1>
            <p className="mt-4 text-muted-foreground font-light max-w-2xl leading-relaxed">
              Edit the canonical Name, Address, Phone and Hours. The right-hand pane previews
              the header, footer and JSON-LD as you type — save when you're happy and every
              visitor gets the new values instantly.
            </p>
            <div className="mt-5 flex flex-wrap gap-4">
              <Link
                to="/admin/embed"
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 rounded-full border border-border text-foreground hover:border-accent hover:text-accent transition-colors"
              >
                Get Wix embed code →
              </Link>
            </div>
          </header>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <form onSubmit={save} className="space-y-10 lg:col-span-7 min-w-0">
            <Section title="Identity">
              <Field label="Clinic name" required>
                <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} required />
              </Field>
              <Field label="Short name" required>
                <input className={inputCls} value={form.short_name} onChange={(e) => set("short_name", e.target.value)} required />
              </Field>
              <Field label="Alternate name (SEO)">
                <input className={inputCls} value={form.alternate_name} onChange={(e) => set("alternate_name", e.target.value)} />
              </Field>
              <Field label="Tagline" full>
                <textarea rows={2} className={inputCls} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} />
              </Field>
            </Section>

            <Section title="Address">
              <Field label="Street" required full>
                <input className={inputCls} value={form.street} onChange={(e) => set("street", e.target.value)} required />
              </Field>
              <Field label="Locality / district" required>
                <input className={inputCls} value={form.locality} onChange={(e) => set("locality", e.target.value)} required />
              </Field>
              <Field label="Region / city" required>
                <input className={inputCls} value={form.region} onChange={(e) => set("region", e.target.value)} required />
              </Field>
              <Field label="Postcode" required>
                <input className={inputCls} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} required />
              </Field>
              <Field label="Country" required>
                <input className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} required />
              </Field>
              <Field label="Country code (2 letters)" required>
                <input maxLength={2} className={inputCls} value={form.country_code} onChange={(e) => set("country_code", e.target.value.toUpperCase())} required />
              </Field>
              <Field label="Latitude">
                <input className={inputCls} value={form.latitude} onChange={(e) => set("latitude", e.target.value)} inputMode="decimal" />
              </Field>
              <Field label="Longitude">
                <input className={inputCls} value={form.longitude} onChange={(e) => set("longitude", e.target.value)} inputMode="decimal" />
              </Field>
            </Section>

            <Section title="Phone & WhatsApp">
              <Field label="Phone (international, e.g. +442033703503)" required error={contactErrors.phone_international}>
                <input
                  className={`${inputCls} ${contactErrors.phone_international ? "border-destructive focus:ring-destructive/40" : ""}`}
                  value={form.phone_international}
                  onChange={(e) => set("phone_international", e.target.value)}
                  aria-invalid={!!contactErrors.phone_international}
                  required
                />
              </Field>
              <Field label="Phone (display)" required error={contactErrors.phone_display}>
                <input
                  className={`${inputCls} ${contactErrors.phone_display ? "border-destructive focus:ring-destructive/40" : ""}`}
                  value={form.phone_display}
                  onChange={(e) => set("phone_display", e.target.value)}
                  aria-invalid={!!contactErrors.phone_display}
                  required
                />
              </Field>
              <Field label="WhatsApp number (international)" error={contactErrors.whatsapp_number}>
                <input
                  className={`${inputCls} ${contactErrors.whatsapp_number ? "border-destructive focus:ring-destructive/40" : ""}`}
                  value={form.whatsapp_number}
                  onChange={(e) => set("whatsapp_number", e.target.value)}
                  aria-invalid={!!contactErrors.whatsapp_number}
                />
              </Field>
              <Field label="WhatsApp (display)">
                <input className={inputCls} value={form.whatsapp_display} onChange={(e) => set("whatsapp_display", e.target.value)} />
              </Field>
              <Field label="WhatsApp link (https://wa.me/…)" full error={contactErrors.whatsapp_url}>
                <input
                  className={`${inputCls} ${contactErrors.whatsapp_url ? "border-destructive focus:ring-destructive/40" : ""}`}
                  value={form.whatsapp_url}
                  onChange={(e) => set("whatsapp_url", e.target.value)}
                  aria-invalid={!!contactErrors.whatsapp_url}
                />
              </Field>
              <Field label="Booking note (e.g. By appointment only)" full>
                <input className={inputCls} value={form.booking_note} onChange={(e) => set("booking_note", e.target.value)} />
              </Field>
            </Section>

            <Section title="Opening hours">
              <div className="md:col-span-2 space-y-4">
                {form.hours.map((row, i) => (
                  <div key={i} className="grid md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-4">
                      <span className={labelCls}>Days label</span>
                      <input className={inputCls} value={row.days} onChange={(e) => setHour(i, { days: e.target.value })} />
                    </div>
                    <div className="md:col-span-3">
                      <span className={labelCls}>Display time</span>
                      <input className={inputCls} value={row.time} onChange={(e) => setHour(i, { time: e.target.value })} />
                    </div>
                    <div className="md:col-span-2">
                      <span className={labelCls}>Opens</span>
                      <input className={inputCls} placeholder="09:00" value={row.opens ?? ""} onChange={(e) => setHour(i, { opens: e.target.value || null })} />
                    </div>
                    <div className="md:col-span-2">
                      <span className={labelCls}>Closes</span>
                      <input className={inputCls} placeholder="18:00" value={row.closes ?? ""} onChange={(e) => setHour(i, { closes: e.target.value || null })} />
                    </div>
                    <div className="md:col-span-1">
                      <span className={labelCls}>Days (schema)</span>
                      <input
                        className={inputCls}
                        placeholder="Mo,Tu"
                        value={row.schema.join(",")}
                        onChange={(e) =>
                          setHour(i, {
                            schema: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                          })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Web & social">
              <Field label="Website URL">
                <input className={inputCls} value={form.website_url} onChange={(e) => set("website_url", e.target.value)} />
              </Field>
              <Field label="Website display text">
                <input className={inputCls} value={form.website_display} onChange={(e) => set("website_display", e.target.value)} />
              </Field>
              <Field label="Instagram URL">
                <input className={inputCls} value={form.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} />
              </Field>
              <Field label="Instagram handle">
                <input className={inputCls} value={form.instagram_handle} onChange={(e) => set("instagram_handle", e.target.value)} />
              </Field>
              <Field label="Booking URL" full>
                <input className={inputCls} value={form.booking_url} onChange={(e) => set("booking_url", e.target.value)} />
              </Field>
            </Section>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground font-light">
                Saving regenerates the on-page NAP and JSON-LD live for every visitor.
              </p>
              <button
                type="submit"
                disabled={saving || contactErrors.hasError || schemaValidation.hasError}
                className="px-7 py-3 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.3em] rounded-full hover:bg-accent transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  contactErrors.hasError
                    ? "Fix the highlighted contact fields first"
                    : schemaValidation.hasError
                      ? `Fix ${schemaValidation.errorCount} JSON-LD ${schemaValidation.errorCount === 1 ? "error" : "errors"} first`
                      : undefined
                }
              >
                {saving ? "Saving…" : "Save & regenerate"}
              </button>
            </div>
          </form>

            <aside className="lg:col-span-5 min-w-0">
              <div className="lg:sticky lg:top-28 space-y-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h2 className="font-display text-xl text-foreground">Live preview</h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {contactErrors.hasError ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border border-destructive bg-destructive/10 text-destructive"
                        role="status"
                        aria-live="polite"
                      >
                        <AlertCircle className="h-3 w-3" />
                        {contactErrors.errorCount} contact{" "}
                        {contactErrors.errorCount === 1 ? "error" : "errors"}
                      </span>
                    ) : (
                      <span
                        className="hidden sm:inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border border-border text-muted-foreground"
                        aria-live="polite"
                      >
                        <CheckCircle2 className="h-3 w-3 text-accent" />
                        Contact OK
                      </span>
                    )}

                    {schemaValidation.hasError ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border border-destructive bg-destructive/10 text-destructive"
                        role="status"
                        aria-live="polite"
                      >
                        <AlertCircle className="h-3 w-3" />
                        {schemaValidation.errorCount} JSON-LD{" "}
                        {schemaValidation.errorCount === 1 ? "error" : "errors"}
                      </span>
                    ) : schemaValidation.warningCount > 0 ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border border-amber-500/60 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                        aria-live="polite"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        {schemaValidation.warningCount} JSON-LD{" "}
                        {schemaValidation.warningCount === 1 ? "warning" : "warnings"}
                      </span>
                    ) : (
                      <span
                        className="hidden sm:inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full border border-border text-muted-foreground"
                        aria-live="polite"
                      >
                        <CheckCircle2 className="h-3 w-3 text-accent" />
                        JSON-LD OK
                      </span>
                    )}

                    <span
                      className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1 rounded-full border ${
                        isDirty
                          ? "border-accent text-accent"
                          : "border-border text-muted-foreground"
                      }`}
                    >
                      {isDirty ? "Unsaved changes" : "In sync"}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground font-light">
                  Mirrors what visitors will see and what Google will read once you save.
                </p>

                {/* Header (compact) preview */}
                <div className="bg-background border border-border rounded-2xl overflow-hidden">
                  <div className="px-5 py-2 border-b border-border bg-linen-deep">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
                      Header
                    </p>
                  </div>
                  <div className="px-5 py-4 flex justify-end">
                    <ClinicNAPPreview clinic={previewClinic} variant="compact" />
                  </div>
                </div>

                {/* Footer (full) preview */}
                <div className="bg-primary text-primary-foreground rounded-2xl overflow-hidden">
                  <div className="px-5 py-2 border-b border-primary-foreground/15">
                    <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Footer</p>
                  </div>
                  <div className="px-5 py-6">
                    <ClinicNAPPreview
                      clinic={previewClinic}
                      variant="full"
                      className="text-primary-foreground"
                    />
                  </div>
                </div>

                {/* JSON-LD preview */}
                <div className="bg-background border border-border rounded-2xl overflow-hidden">
                  <div className="px-5 py-2 border-b border-border bg-linen-deep flex items-center justify-between">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
                      JSON-LD <span className="opacity-60">@graph</span>
                    </p>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={reRunNapCheck}
                        disabled={napChecking}
                        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 disabled:opacity-50"
                        title="Re-validate every NAP field against the JSON-LD that will be published"
                      >
                        <RefreshCw
                          className={`h-3 w-3 ${napChecking ? "animate-spin" : ""}`}
                        />
                        Re-run NAP check
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              JSON.stringify(previewGraph, null, 2),
                            );
                            toast({
                              title: "JSON-LD copied",
                              description:
                                "Paste it into the 'Code' tab of Google's Rich Results Test.",
                            });
                          } catch {
                            toast({
                              title: "Copy failed",
                              description: "Select the text manually instead.",
                              variant: "destructive",
                            });
                          }
                          window.open(
                            "https://search.google.com/test/rich-results",
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                        className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70"
                        title="Copies the current JSON-LD and opens Google's Rich Results Test (Code tab)"
                      >
                        Test in Google ↗
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(
                              JSON.stringify(previewGraph, null, 2),
                            );
                            toast({ title: "Copied", description: "JSON-LD copied to clipboard." });
                          } catch {
                            toast({
                              title: "Copy failed",
                              description: "Select the text manually instead.",
                              variant: "destructive",
                            });
                          }
                        }}
                        className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <pre className="px-5 py-4 text-[11px] leading-relaxed text-foreground/85 font-mono max-h-[420px] overflow-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(previewGraph, null, 2)}
                  </pre>
                </div>

                {/* Schema validation panel */}
                <SchemaValidationPanel
                  issues={schemaValidation.issues}
                  errorCount={schemaValidation.errorCount}
                  warningCount={schemaValidation.warningCount}
                />

                {/* NAP cross-check panel */}
                <NapCheckPanel
                  result={napCheck}
                  onReRun={reRunNapCheck}
                  busy={napChecking}
                />
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section>
    <h2 className="font-display text-xl text-foreground mb-5 pb-3 border-b border-border">
      {title}
    </h2>
    <div className="grid md:grid-cols-2 gap-5">{children}</div>
  </section>
);

const Field = ({
  label,
  children,
  required,
  full,
  error,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  full?: boolean;
  error?: string | null;
}) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
      {label}
      {required ? " *" : ""}
    </span>
    {children}
    {error ? (
      <span className="mt-1.5 block text-[11px] text-destructive font-light leading-snug">
        {error}
      </span>
    ) : null}
  </label>
);

const SchemaValidationPanel = ({
  issues,
  errorCount,
  warningCount,
}: {
  issues: SchemaIssue[];
  errorCount: number;
  warningCount: number;
}) => {
  if (issues.length === 0) {
    return (
      <div className="bg-background border border-border rounded-2xl p-5 flex items-center gap-3">
        <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
        <p className="text-xs text-muted-foreground font-light">
          JSON-LD looks complete — no missing or malformed fields detected.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-background border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-2 border-b border-border bg-linen-deep flex items-center justify-between">
        <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
          JSON-LD validation
        </p>
        <p className="text-[10px] tracking-[0.2em] uppercase">
          {errorCount > 0 && (
            <span className="text-destructive mr-3">{errorCount} error{errorCount === 1 ? "" : "s"}</span>
          )}
          {warningCount > 0 && (
            <span className="text-amber-600 dark:text-amber-400">{warningCount} warning{warningCount === 1 ? "" : "s"}</span>
          )}
        </p>
      </div>
      <ul className="divide-y divide-border max-h-[320px] overflow-auto">
        {issues.map((issue, i) => (
          <li key={i} className="px-5 py-3 flex items-start gap-3">
            {issue.severity === "error" ? (
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                {issue.node}
                {issue.path && <span className="opacity-70"> · {issue.path}</span>}
              </p>
              <p className="text-sm text-foreground font-light leading-snug mt-0.5">
                {issue.message}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const NapCheckPanel = ({
  result,
  onReRun,
  busy,
}: {
  result: NapPreviewResult;
  onReRun: () => void;
  busy: boolean;
}) => {
  const failing = result.items.filter((i) => !i.ok);
  const passing = result.items.filter((i) => i.ok);
  return (
    <div className="bg-background border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-2 border-b border-border bg-linen-deep flex items-center justify-between gap-3">
        <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
          NAP cross-check
        </p>
        <div className="flex items-center gap-3">
          {result.ok ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-accent">
              <CheckCircle2 className="h-3 w-3" /> All {result.items.length} fields match
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-destructive">
              <AlertCircle className="h-3 w-3" />
              {result.errorCount} mismatch{result.errorCount === 1 ? "" : "es"}
            </span>
          )}
          <button
            type="button"
            onClick={onReRun}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
            Re-run
          </button>
        </div>
      </div>

      {failing.length > 0 && (
        <ul className="divide-y divide-border max-h-[280px] overflow-auto">
          {failing.map((item) => (
            <li
              key={item.field}
              className="px-5 py-3 flex items-start gap-3 bg-destructive/5"
            >
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                  {item.label}
                </p>
                <p className="text-sm text-foreground font-light leading-snug mt-0.5">
                  {item.reason}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] font-mono">
                  <div className="rounded-md bg-background border border-border px-2 py-1.5">
                    <span className="block text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                      Form
                    </span>
                    <span className="break-all text-foreground">
                      {item.expected ?? "—"}
                    </span>
                  </div>
                  <div className="rounded-md bg-background border border-destructive/40 px-2 py-1.5">
                    <span className="block text-[9px] uppercase tracking-[0.2em] text-destructive">
                      JSON-LD
                    </span>
                    <span className="break-all text-foreground">
                      {item.schema ?? "—"}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {passing.length > 0 && (
        <details className="border-t border-border">
          <summary className="px-5 py-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground cursor-pointer hover:text-foreground">
            {passing.length} passing field{passing.length === 1 ? "" : "s"}
          </summary>
          <ul className="divide-y divide-border max-h-[200px] overflow-auto">
            {passing.map((item) => (
              <li key={item.field} className="px-5 py-2 flex items-center gap-3">
                <CheckCircle2 className="h-3.5 w-3.5 text-accent shrink-0" />
                <span className="text-[11px] text-muted-foreground">
                  {item.label}
                </span>
                <span className="ml-auto text-[10px] font-mono text-muted-foreground/70 truncate max-w-[55%]">
                  {item.expected ?? "—"}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      <p className="px-5 py-2 border-t border-border text-[10px] text-muted-foreground/80 font-light">
        Last run {new Date(result.checkedAt).toLocaleTimeString()}.
      </p>
    </div>
  );
};

export default ClinicSettings;
